"use client";

import { useState } from "react";
import ModernDialog from "@/components/ui/ModernDialog";
import { useAuth } from "@/context/AuthContext";
import { sendOtp, updateUserProfile, verifyOtp } from "@/lib/auth";
import { isEmailVerified, isPhoneVerified } from "@/lib/userVerification";

const OTP_LENGTH = 5;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizePhoneForApi(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return "";
  }

  const digits = trimmed.replace(/\D/g, "");
  if (!digits) {
    return "";
  }

  if (digits.startsWith("62")) {
    return `+${digits}`;
  }

  if (digits.startsWith("0")) {
    return `+62${digits.slice(1)}`;
  }

  if (digits.startsWith("8")) {
    return `+62${digits}`;
  }

  return `+${digits}`;
}

function StatusPill({ verified }) {
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${
        verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
      }`}
    >
      {verified ? "Verified" : "Unverified"}
    </span>
  );
}

export default function ProfileSettingsDialog() {
  const {
    user,
    closeProfileSettings,
    showToast,
    refreshUser,
    loginData,
  } = useAuth();

  const [baseline, setBaseline] = useState(() => ({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
  }));
  const [nameValue, setNameValue] = useState(() => user?.name || "");
  const [emailValue, setEmailValue] = useState(() => user?.email || "");
  const [phoneValue, setPhoneValue] = useState(() => user?.phone || "");
  const [pendingVerificationUpdates, setPendingVerificationUpdates] = useState({
    email: "",
    phone: "",
  });
  const [otpChannel, setOtpChannel] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [loadingAction, setLoadingAction] = useState("");

  const emailVerified = isEmailVerified(user);
  const phoneVerified = isPhoneVerified(user);
  const phoneReadOnly = phoneVerified;

  const normalizedEmail = emailValue.trim().toLowerCase();
  const normalizedBaselineEmail = baseline.email.trim().toLowerCase();
  const normalizedPhone = phoneValue.trim();
  const normalizedPhoneForOtp = normalizePhoneForApi(normalizedPhone);
  const normalizedBaselinePhoneForOtp = normalizePhoneForApi(baseline.phone);

  const hasNameChanged = nameValue.trim() !== baseline.name;
  const hasEmailChanged = normalizedEmail !== normalizedBaselineEmail;
  const hasPhoneChanged = normalizedPhoneForOtp !== normalizedBaselinePhoneForOtp;
  const channelPendingUpdate = pendingVerificationUpdates[otpChannel] || "";
  const isOtpInputActive = otpChannel === "email" || otpChannel === "phone";

  async function handleSaveProfile() {
    if (!user?.id) {
      showToast("User tidak ditemukan.");
      return;
    }

    if (!hasNameChanged) {
      if (hasEmailChanged || hasPhoneChanged) {
        showToast("Untuk email/nomor, klik Kirim OTP lalu verifikasi supaya perubahan tersimpan.");
      } else {
        showToast("Data berhasil disimpan.");
        closeProfileSettings();
      }
      return;
    }

    const nextName = nameValue.trim();
    if (!nextName) {
      showToast("Nama lengkap tidak boleh kosong.");
      return;
    }

    setLoadingAction("save-profile");
    try {
      await updateUserProfile(user.id, { name: nextName });
      setBaseline((prev) => ({ ...prev, name: nextName }));
      await refreshUser();
      showToast("Data berhasil disimpan.");
      closeProfileSettings();
    } catch (error) {
      const message = error?.payload?.status?.message || error?.message || "Gagal memperbarui nama.";
      showToast(message);
    } finally {
      setLoadingAction("");
    }
  }

  function resolveChannelValueForOtp(channel) {
    if (channel === "email") {
      if (!normalizedEmail) {
        showToast("Email tidak boleh kosong.");
        throw new Error("invalid_email");
      }
      if (!EMAIL_PATTERN.test(normalizedEmail)) {
        showToast("Format email tidak valid.");
        throw new Error("invalid_email_format");
      }
      return normalizedEmail;
    }

    if (channel === "phone") {
      if (!normalizedPhone) {
        showToast("Nomor WhatsApp tidak boleh kosong.");
        throw new Error("invalid_phone");
      }
      return normalizePhoneForApi(normalizedPhone);
    }

    return "";
  }

  async function handleSendOtp(channel) {
    if (!user?.id) {
      showToast("User tidak ditemukan.");
      return;
    }

    if (channel === "email" && !emailValue.trim()) {
      showToast("Isi email terlebih dahulu.");
      return;
    }

    if (channel === "phone" && !phoneValue.trim()) {
      showToast("Isi nomor WhatsApp terlebih dahulu.");
      return;
    }

    setLoadingAction(`send-${channel}`);
    try {
      const channelValue = resolveChannelValueForOtp(channel);
      const hasChannelChanged = channel === "email" ? hasEmailChanged : hasPhoneChanged;
      const payload = { channel };

      if (hasChannelChanged && channelValue) {
        await updateUserProfile(user.id, { [channel]: channelValue });
        setBaseline((prev) => ({
          ...prev,
          [channel]: channelValue,
        }));
        await refreshUser();
        payload[channel] = channelValue;
      }

      await sendOtp(user.id, payload);
      await refreshUser();
      setPendingVerificationUpdates((prev) => ({
        ...prev,
        [channel]: hasChannelChanged ? channelValue : "",
      }));
      setOtpChannel(channel);
      setOtpValue("");
      showToast(`OTP ${channel === "email" ? "email" : "WhatsApp"} berhasil dikirim.`);
    } catch (error) {
      if (
        error?.message === "invalid_email"
        || error?.message === "invalid_email_format"
        || error?.message === "invalid_phone"
      ) {
        return;
      }
      const message = error?.payload?.status?.message || error?.message || "Gagal mengirim OTP.";
      showToast(message);
    } finally {
      setLoadingAction("");
    }
  }

  async function handleVerifyOtp() {
    if (!user?.id || !otpChannel) {
      return;
    }

    if (otpValue.trim().length !== OTP_LENGTH) {
      showToast("Masukkan 5 digit OTP.");
      return;
    }

    setLoadingAction(`verify-${otpChannel}`);
    try {
      const verifyingChannel = otpChannel;
      const pendingValue = pendingVerificationUpdates[verifyingChannel];
      const payload = {
        channel: verifyingChannel,
        otp: otpValue.trim(),
      };

      if (pendingValue) {
        payload[verifyingChannel] = pendingValue;
      }

      const response = await verifyOtp(user.id, {
        ...payload,
      });

      if (pendingValue) {
        if (verifyingChannel === "phone") {
          setPhoneValue(pendingValue);
        } else {
          setEmailValue(pendingValue);
        }
        setBaseline((prev) => ({
          ...prev,
          [verifyingChannel]: pendingValue,
        }));
      }

      const token = response?.data?.token || response?.token;
      const expiresIn = response?.data?.expiresIn || response?.expiresIn;
      if (token) {
        loginData(token, expiresIn);
      }

      await refreshUser();
      setOtpValue("");
      setOtpChannel("");
      setPendingVerificationUpdates((prev) => ({
        ...prev,
        [verifyingChannel]: "",
      }));
      showToast(`${verifyingChannel === "email" ? "Email" : "Nomor WhatsApp"} berhasil diverifikasi.`);
    } catch (error) {
      const message = error?.payload?.status?.message || error?.message || "OTP tidak valid.";
      showToast(message);
    } finally {
      setLoadingAction("");
    }
  }

  return (
    <ModernDialog
      isOpen
      onClose={closeProfileSettings}
      title="Pengaturan Akun"
      description="Nama disimpan manual. Email/nomor akan disimpan setelah OTP verifikasi berhasil."
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="profileName" className="text-xs font-semibold text-zinc-600">Nama Lengkap</label>
          <input
            id="profileName"
            name="name"
            autoComplete="name"
            type="text"
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            placeholder="Nama lengkap"
            className="rounded-xl border border-zinc-200 px-4 py-2.5 text-sm text-zinc-900 transition focus:border-primary focus:outline-none"
          />
        </div>

        <div className="rounded-xl border border-zinc-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <label htmlFor="profileEmail" className="text-xs font-semibold text-zinc-700">Email</label>
            <StatusPill verified={emailVerified} />
          </div>
          <input
            id="profileEmail"
            name="email"
            autoComplete="email"
            type="email"
            value={emailValue}
            onChange={(e) => setEmailValue(e.target.value)}
            placeholder="kamu@email.com"
            readOnly={emailVerified}
            className={`w-full rounded-xl border px-4 py-2.5 text-sm transition focus:outline-none ${
              emailVerified
                ? "cursor-not-allowed border-zinc-200 bg-zinc-100 text-zinc-500"
                : "border-zinc-200 text-zinc-900 focus:border-primary"
            }`}
          />
          {!emailVerified && (
            <div className="mt-2 flex w-full flex-col gap-1.5">
              <button
                type="button"
                onClick={() => handleSendOtp("email")}
                disabled={loadingAction === "send-email"}
                className="w-full rounded-lg cursor-pointer border border-primary px-3 py-2 text-xs font-semibold text-primary transition hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loadingAction === "send-email" ? "Mengirim..." : "Kirim OTP"}
              </button>
              {hasEmailChanged && (
                <p className="text-[11px] text-zinc-500">
                  Perubahan email akan tersimpan setelah OTP email berhasil diverifikasi.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-zinc-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <label htmlFor="profilePhone" className="text-xs font-semibold text-zinc-700">No. WhatsApp</label>
            <StatusPill verified={phoneVerified} />
          </div>
          <input
            id="profilePhone"
            name="phone"
            autoComplete="tel"
            type="tel"
            value={phoneValue}
            onChange={(e) => setPhoneValue(e.target.value)}
            placeholder="+628xxxxxxxxxx"
            readOnly={phoneReadOnly}
            className={`w-full rounded-xl border px-4 py-2.5 text-sm transition focus:outline-none ${
              phoneReadOnly
                ? "cursor-not-allowed border-zinc-200 bg-zinc-100 text-zinc-500"
                : "border-zinc-200 text-zinc-900 focus:border-primary"
            }`}
          />
          {!phoneVerified && (
            <div className="mt-2 flex w-full flex-col gap-1.5">
              <button
                type="button"
                onClick={() => handleSendOtp("phone")}
                disabled={loadingAction === "send-phone"}
                className="w-full cursor-pointer rounded-lg border border-primary px-3 py-2 text-xs font-semibold text-primary transition hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loadingAction === "send-phone" ? "Mengirim..." : "Kirim OTP"}
              </button>
              {hasPhoneChanged && (
                <p className="text-[11px] text-zinc-500">
                  Perubahan nomor akan tersimpan setelah OTP WhatsApp berhasil diverifikasi.
                </p>
              )}
            </div>
          )}
        </div>

        {isOtpInputActive && (
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
            <label htmlFor="profileOtp" className="text-xs font-semibold text-zinc-700 block mb-1">
              Verifikasi {otpChannel === "email" ? "Email" : "No. WhatsApp"}
            </label>
            {channelPendingUpdate && (
              <p className="text-[11px] text-zinc-500 mb-1">
                Data baru akan disimpan setelah OTP ini berhasil diverifikasi.
              </p>
            )}
            <div className="mt-2 flex items-center gap-2">
              <input
                id="profileOtp"
                name="otp"
                autoComplete="one-time-code"
                type="text"
                inputMode="numeric"
                maxLength={OTP_LENGTH}
                value={otpValue}
                onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH))}
                placeholder="Masukkan 5 digit OTP"
                className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm text-zinc-900 transition focus:border-primary focus:outline-none"
              />
              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={loadingAction === `verify-${otpChannel}`}
                className="rounded-xl cursor-pointer bg-primary px-4 py-2.5 text-xs font-semibold text-white transition hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loadingAction === `verify-${otpChannel}` ? "Verifying..." : "Verify"}
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={closeProfileSettings}
            className="flex-1 rounded-xl cursor-pointer border border-zinc-200 py-2.5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50"
          >
            Tutup
          </button>
          <button
            type="button"
            onClick={handleSaveProfile}
            disabled={loadingAction === "save-profile"}
            className="flex-1 rounded-xl cursor-pointer bg-primary py-2.5 text-sm font-semibold text-white transition hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loadingAction === "save-profile" ? "Menyimpan..." : "Simpan Data"}
          </button>
        </div>
      </div>
    </ModernDialog>
  );
}
